import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  GsFormContentTab,
  GsFormField,
  GsFormFieldBasic,
  GsFormFieldResponsive,
  GsFormSchema,
  GsFormWithFields
} from 'libs/shared/src/components';
import { IDialog, IInputConstraint, IInputRender, InputType, TextBoxInputType } from 'libs/shared/src/models';
import { from, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { FileService } from '../../file/file.service';
import { SchemaService } from '../schemas/schema.service';
import { InputValidationService } from '../validators/input-validation.service';
import { FormsMapService } from './forms-map.service';
import { iconNameFromFontAwesome } from '../../../utils/fontawesome-icon.util';

export const NON_VALUE_INPUT_TYPES: InputType[] = [
  InputType.Break,
  InputType.Button,
  InputType.Headline,
  InputType.HtmlBox,
  InputType.Label,
  InputType.Separator,
  InputType.Spacer
];

@Injectable({
  providedIn: 'root'
})
export class FormsService {
  // Services
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly fileService: FileService = inject(FileService);
  private readonly formsMapService: FormsMapService = inject(FormsMapService);
  private readonly inputValidationService: InputValidationService = inject(InputValidationService);

  // WeakMap to store destroy subjects for each form group (auto-cleanup on garbage collection)
  private readonly formGroupDestroy$ = new WeakMap<FormGroup, Subject<void>>();

  // Constants
  private static readonly DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

  /**
   * Filters out unuseable inputs (set them as isInput = false)
   * @param inputs to update
   * @returns the updated inputs
   */
  checkInputs(inputs?: IInputRender[]): IInputRender[] {
    if (!inputs) return [];
    return inputs.map((input) => {
      input.isInput = input.name?.length > 0 && !NON_VALUE_INPUT_TYPES.includes(input.inputType);
      return input;
    });
  }

  /**
   * Generates the rows from the inputs
   * @param inputs to generate rows from
   * @returns the rows with the corresponding inputs
   */
  inputRows(inputs?: IInputRender[]): IInputRender[][] {
    if (!inputs) return [];
    inputs = this.checkRepeatInputs(inputs);
    const rows: Record<string, IInputRender[]> = {};
    inputs.forEach((input, index) => {
      if (!input.order || input.order === 0) input.order = index;
      if (!input.row) input.row = index.toString();
      if (!rows[input.row]) rows[input.row] = [];
      rows[input.row].push(input);
    });
    return Object.values(rows).sort((a, b) => {
      return a && b ? a[0].order - b[0].order : 0;
    });
  }

  /**
   * Builds a form group from a list of inputs
   * @param inputs to build the form from
   * @returns the form group
   */
  buildFormFromInputs(inputs?: IInputRender[]): FormGroup {
    inputs = this.checkInputs(inputs);
    inputs = this.checkRepeatInputs(inputs);
    if (!inputs || inputs.length === 0) return new FormGroup({});
    const formGroup = new FormGroup({});
    const destroy$ = new Subject<void>();
    this.formGroupDestroy$.set(formGroup, destroy$);

    inputs.forEach((input) => {
      const formControl = new FormControl({
        value: this.mapDefaultValue(input),
        disabled: input.readonly || !input.isInput
      });
      formGroup.addControl(input.name, formControl);
    });
    inputs.forEach((input) => {
      const formControl = formGroup.get([input.name]);
      if (formControl) {
        this.inputValidationService.generateValidators(formControl, input, formGroup);
      }
    });
    this.setupRepeatRevalidation(inputs, formGroup, destroy$);
    this.setupBlockRevalidation(inputs, formGroup, destroy$);
    return formGroup;
  }

  /**
   * Cleans up subscriptions for a form group created by buildFormFromInputs.
   * Call this when the form group is no longer needed (e.g., in ngOnDestroy).
   * @param formGroup - The form group to clean up.
   */
  destroyForm(formGroup: FormGroup): void {
    const destroy$ = this.formGroupDestroy$.get(formGroup);
    if (destroy$) {
      destroy$.next();
      destroy$.complete();
      this.formGroupDestroy$.delete(formGroup);
    }
  }

  /**
   * Sets up valueChanges listeners on original fields to revalidate their repeat/confirmation fields.
   * @param inputs - The list of inputs to check for repeat constraints.
   * @param formGroup - The form group containing the controls.
   * @param destroy$ - Subject to signal when subscriptions should be cleaned up.
   */
  private setupRepeatRevalidation(inputs: IInputRender[], formGroup: FormGroup, destroy$: Subject<void>): void {
    inputs.forEach((input) => {
      const repeatConstraint = input.constraints?.find((c) => c.type === 'InputConstraintRepeat' && c.otherInputField);
      if (repeatConstraint?.otherInputField) {
        const otherControl = formGroup.get([repeatConstraint.otherInputField]);
        const currentControl = formGroup.get([input.name]);
        if (otherControl && currentControl) {
          otherControl.valueChanges.pipe(takeUntil(destroy$)).subscribe(() => {
            currentControl.updateValueAndValidity({ emitEvent: false });
          });
        }
      }
    });
  }

  /**
   * Sets up valueChanges listeners between the fields of an `InputConstraintBlock` ("at least one of
   * these"). Filling one field has to clear the error on its siblings, which only revalidate when told.
   * @param inputs - The list of inputs to check for block constraints.
   * @param formGroup - The form group containing the controls.
   * @param destroy$ - Subject to signal when subscriptions should be cleaned up.
   */
  private setupBlockRevalidation(inputs: IInputRender[], formGroup: FormGroup, destroy$: Subject<void>): void {
    inputs.forEach((input) => {
      const blockFields = input.constraints?.find((c) => c.type === 'InputConstraintBlock')?.blockFields;
      const currentControl = formGroup.get([input.name]);
      if (!blockFields?.length || !currentControl) return;
      blockFields
        .filter((name) => name !== input.name)
        .forEach((name) => {
          const sibling = formGroup.get([name]);
          // `emitEvent: false` keeps the siblings from revalidating each other in a loop.
          sibling?.valueChanges
            .pipe(takeUntil(destroy$))
            .subscribe(() => currentControl.updateValueAndValidity({ emitEvent: false }));
        });
    });
  }

  /**
   * Automatically generate a form group based on the form schema.
   * @returns The form group with form controls based on the form schema.
   */
  generateFormGroup(schema?: GsFormWithFields): FormGroup {
    const formGroup: { [key: string]: AbstractControl } = {};
    if (!schema) return this.fb.group(formGroup);
    schema.fields?.forEach((field) => {
      if (SchemaService.isGsFormFieldBasic(field) && field.isResponsive) {
        formGroup[field.formControlName] = this.generateResponsiveFormGroup(field);
      } else if (SchemaService.isGsFormController(field)) {
        formGroup[field.formControlName] = new FormControl({
          value: this.getDefaultValue(field),
          disabled: !!field.readonly
        });
      }
    });
    const fg = this.fb.group(formGroup);
    // Apply constraints from schema fields as Angular validators
    this.applySchemaConstraints(schema.fields, fg);
    return fg;
  }

  /**
   * Applies IInputConstraint validators from schema fields to the FormGroup.
   * Called after all FormControls are created so cross-field validators (e.g. repeat) can reference other controls.
   * @param fields - The schema fields to check for constraints.
   * @param formGroup - The FormGroup containing the controls.
   */
  private applySchemaConstraints(fields: GsFormField<unknown>[] | undefined, formGroup: FormGroup): void {
    if (!fields) return;
    fields.forEach((field) => {
      if (SchemaService.isGsFormController(field) && 'constraints' in field) {
        const constraints = (field as { constraints?: IInputConstraint[] }).constraints;
        if (constraints?.length) {
          const control = formGroup.get([field.formControlName]);
          if (control) {
            this.inputValidationService.applyConstraints(control, constraints, formGroup);
          }
        }
      }
    });
  }

  /**
   * Maps a dialog to a form schema (used for the designer-backend-settings)
   * @param dialog from backend to map to a form schema
   * @returns the form schema
   */
  dialogToSchema(dialog: IDialog): GsFormSchema {
    const fields = dialog.inputs
      .map((input) => this.formsMapService.mapInputToField(input))
      .filter((field) => Object.keys(field).length > 0);

    // Extract unique tabs preserving original order
    const tabMap = new Map<string, GsFormContentTab>();
    for (const input of dialog.inputs) {
      if (input.tabSheet?.name && !tabMap.has(input.tabSheet.name)) {
        tabMap.set(input.tabSheet.name, {
          name: input.tabSheet.name,
          caption: input.tabSheet.caption,
          glyph: iconNameFromFontAwesome(input.tabSheet.glyph)
        });
      }
    }

    let tabs: GsFormContentTab[] | undefined;
    if (tabMap.size > 0) {
      // Check if any mapped fields are unassigned (no tabName)
      const hasUnassignedFields = fields.some((f) => SchemaService.isGsFormFieldBasic(f) && !f.tabName);
      // Only add fallback "Allgemein" tab if there are actually fields that need it
      if (hasUnassignedFields && !tabMap.has('general')) {
        tabMap.set('general', { name: 'general', caption: 'gsForm.tabs.general', glyph: 'gear' });
      }
      tabs = [...tabMap.values()];
      // Assign unassigned fields to the "general" tab
      if (hasUnassignedFields) {
        for (const field of fields) {
          if (SchemaService.isGsFormFieldBasic(field) && !field.tabName) {
            (field as GsFormFieldBasic<unknown>).tabName = 'general';
          }
        }
      }
    }

    return { header: dialog.title, fields, tabs };
  }

  /**
   * Subscribes to FileUpload inputs on a form and converts selected files to Base64 data URLs.
   * Returns a Map that is kept in sync with the current file selections. The caller should
   * pass this map to `formToPayload` so that File values are replaced with their Base64 strings.
   *
   * @param form      The form group containing the file controls
   * @param inputs    The input definitions (only FileUpload entries are processed)
   * @param until$    A Subject that signals when the subscriptions should be torn down
   * @returns A live Map<string, string> updated on every file change
   */
  trackFileInputs(form: FormGroup, inputs: IInputRender[] | undefined, until$: Subject<void>): Map<string, string> {
    const fileBase64Map = new Map<string, string>();
    const fileInputDefs = (inputs ?? []).filter((i) => i.inputType === InputType.FileUpload);

    for (const fileInputDef of fileInputDefs) {
      form
        .get([fileInputDef.name])
        ?.valueChanges.pipe(
          takeUntil(until$),
          switchMap((file: File | null) =>
            file instanceof File ? from(this.fileService.fileToBase64(file)).pipe(catchError(() => of(null))) : of(null)
          )
        )
        .subscribe((base64: string | null) => {
          if (base64 !== null) {
            fileBase64Map.set(fileInputDef.name, base64);
          } else {
            fileBase64Map.delete(fileInputDef.name);
          }
        });
    }

    return fileBase64Map;
  }

  /**
   * Converts a form to a payload object.
   * When a `fileBase64Map` is provided, File values are replaced with their Base64 data URLs.
   * @param form the form group to convert
   * @param inputs optional inputs to check against
   * @param fileBase64Map optional map of field names to Base64 strings (from `trackFileInputs`)
   * @returns the payload object with the form values
   */
  formToPayload(form: FormGroup, inputs?: IInputRender[], fileBase64Map?: Map<string, string>): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    inputs = this.checkInputs(inputs);

    inputs.forEach((input) => {
      // An alert carries no value, and its name is a fresh guid on every build.
      if (input.isInput && input.customType !== 'Alert') {
        const control = form.get([input.name]);
        if (input.inputType === InputType.FileUpload) {
          // CS4-2453: A posted null means "clear this field" and would wipe the template's own
          // value, so an untouched field posts nothing. Only a dirty field posts null.
          const base64 = fileBase64Map?.get(input.name);
          if (base64 != null) {
            payload[input.name] = base64;
          } else if (control?.dirty) {
            payload[input.name] = null;
          }
        } else {
          payload[input.name] = this.mapTextBoxValue(input, control?.value);
        }
      }
    });

    return payload;
  }

  /**
   * Gets the default value for a field.
   * @param field to get the default value for
   * @returns the default value for the field
   */
  getDefaultValue(field: GsFormField<unknown>): unknown {
    if (field.default == null && field.nullable) {
      return undefined;
    }
    return field.default;
  }

  /**
   * Parses a string into a Date object.
   * If the string matches the DATE_PATTERN (yyyy-MM-dd), the time is set to midnight (00:00:00).
   * Otherwise, the string is passed directly to the Date constructor.
   * @param value The raw string value to parse
   * @returns A Date object based on the input string
   */
  private parseAsDate(value: string): Date {
    if (FormsService.DATE_PATTERN.test(value)) {
      return new Date(value + 'T00:00:00');
    }
    return new Date(value);
  }

  /**
   * Maps the value of a TextBox input to the correct date-time format
   * only if the TextBoxInputType represents a date or datetime field.
   * For other input types the value is returned unchanged.
   * @param input The TextBox input definition with metadata about its type
   * @param value The raw value from the form control
   * @returns The mapped value as a formatted date-time string, or the original value
   */
  private mapTextBoxValue(input: IInputRender, value: unknown): unknown {
    if (!this.isTypeDate(input)) return value;

    if (typeof value === 'string' && value.trim() !== '') {
      value = this.parseAsDate(value);
    }

    if (!(value instanceof Date) || isNaN(value.getTime())) {
      return value;
    }

    switch (input.textBoxInputType) {
      case TextBoxInputType.Date:
        return this.toLocalISOString(new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0));

      case TextBoxInputType.Datetime:
      case TextBoxInputType.Datetime_local:
        return this.toLocalISOString(value);

      default:
        return value;
    }
  }

  /**
   * Converts a Date object into a local ISO-like string without timezone information.
   * @param date The Date object to convert
   * @returns The formatted local ISO string
   */
  private toLocalISOString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  /**
   * Generates a responsive form group for a field
   * @param field to generate the responsive form group for
   * @returns the responsive form group
   */
  private generateResponsiveFormGroup<T = unknown>(field: GsFormFieldResponsive<T>): FormGroup<any> {
    const defaultValues = field.nullable ? (field.default == undefined ? undefined : field.default) : field.default;
    return this.fb.group({
      default: new FormControl(defaultValues ? defaultValues.default : undefined),
      xs: new FormControl(defaultValues ? defaultValues.xs : undefined),
      sm: new FormControl(defaultValues ? defaultValues.sm : undefined),
      md: new FormControl(defaultValues ? defaultValues.md : undefined),
      lg: new FormControl(defaultValues ? defaultValues.lg : undefined),
      xl: new FormControl(defaultValues ? defaultValues.xl : undefined),
      xxl: new FormControl(defaultValues ? defaultValues.xxl : undefined)
    });
  }

  /**
   * Checks if the input is of type date.
   * @param input to check for the correct type
   * @returns true if the input is of type date, false otherwise
   */
  private isTypeDate(input: IInputRender): boolean {
    return (
      input.inputType === InputType.TextBox &&
      (input.textBoxInputType === TextBoxInputType.Date ||
        input.textBoxInputType === TextBoxInputType.Datetime ||
        input.textBoxInputType === TextBoxInputType.Datetime_local)
    );
  }

  /**
   * Maps the default value of an input to the correct type.
   * @param input The input definition with metadata about its type
   * @returns The mapped default value, or null if not applicable
   */
  private mapDefaultValue(input: IInputRender): IInputRender['value'] {
    if (!input.isInput) return null;
    if (this.isTypeDate(input)) {
      return typeof input.value === 'string' ? input.value.split('T')[0] : null;
    }
    return input.value;
  }

  /**
   * Checks if there are any repeat inputs and copies properties from the original input
   * only if the repeat input doesn't already have its own constraints from the backend.
   * @param inputs to check
   * @returns the updated inputs
   */
  private checkRepeatInputs(inputs: IInputRender[]): IInputRender[] {
    if (!inputs) return [];
    const repeatInputs = inputs.filter((input) => input.name.includes('_repeat'));
    repeatInputs.forEach((repeatInput) => {
      const originalInputName = repeatInput.name.replace('_repeat', '');
      const originalInput = inputs.find((i) => i.name === originalInputName);
      if (originalInput) {
        if (!repeatInput.constraints || repeatInput.constraints.length === 0) {
          repeatInput.constraints = originalInput.constraints;
        }
        repeatInput.readonly = repeatInput.readonly ?? originalInput.readonly;
        repeatInput.required = repeatInput.required ?? originalInput.required;
        repeatInput.isVisible = repeatInput.isVisible ?? originalInput.isVisible;
        repeatInput.updateEnabled = repeatInput.updateEnabled ?? originalInput.updateEnabled;
      }
    });
    return inputs;
  }
}
